import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, type DocRow } from '../api';
import { meta } from '../module';
import { Loading, Empty, ErrorState, StateChip, StageBar } from '../components/ui';

/**
 * The screen people open most often.
 *
 * Filters live in the URL rather than in component state: a register
 * view that cannot be pasted into a message to a colleague is half a
 * feature.
 *
 * eng. groups by discipline because that is how engineering work is
 * owned and chased; pm. keeps one flat list because a document
 * register is read by number.
 */
export function Register() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<DocRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const q = params.get('q') ?? '';
  const state = params.get('state') ?? '';
  const stage = params.get('stage') ?? '';

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  async function load() {
    setRows(null);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('kind', meta.kind);
      qs.set('perPage', '100');
      if (q) qs.set('q', q);
      if (state) qs.set('state', state);
      if (stage) qs.set('stage', stage);
      const res = await api.get<{ documents: DocRow[]; total: number }>(`/documents?${qs}`);
      setRows(res.documents);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the register.');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, state, stage]);

  function open(id: string) {
    navigate(`/documents/${id}`);
  }

  function Rows({ list }: { list: DocRow[] }) {
    return (
      <>
        {list.map((d) => (
          <tr
            key={d.id}
            onClick={() => open(d.id)}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && open(d.id)}
          >
            <td className="num">{d.docNumber}</td>
            <td>{d.title}</td>
            <td>{d.docType?.code ?? '—'}</td>
            {!meta.groupByDiscipline && <td>{d.discipline?.code ?? '—'}</td>}
            {meta.usesStages && (
              <td>
                <StageBar stage={d.currentStage} />
              </td>
            )}
            <td className="num">{d.currentRevCode ?? '—'}</td>
            <td>
              <StateChip state={d.state} />
            </td>
          </tr>
        ))}
      </>
    );
  }

  function Head() {
    return (
      <thead>
        <tr>
          <th>Number</th>
          <th>Title</th>
          <th>Type</th>
          {!meta.groupByDiscipline && <th>Discipline</th>}
          {meta.usesStages && <th>Stage</th>}
          <th>Rev</th>
          <th>Status</th>
        </tr>
      </thead>
    );
  }

  // Group only when the module asks for it, and keep an "Unassigned"
  // bucket rather than dropping rows that have no discipline.
  const groups = new Map<string, { label: string; rows: DocRow[] }>();
  if (rows && meta.groupByDiscipline) {
    for (const d of rows) {
      const key = d.discipline?.code ?? 'ZZZ';
      const label = d.discipline?.name ?? 'Unassigned';
      const g = groups.get(key) ?? { label, rows: [] };
      g.rows.push(d);
      groups.set(key, g);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{meta.nounPlural}</h1>
          <p>{total} in this register</p>
        </div>
      </div>

      <div className="filters">
        <input
          className="input"
          placeholder="Search number or title…"
          defaultValue={q}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setParam('q', (e.target as HTMLInputElement).value.trim());
          }}
          aria-label={`Search ${meta.nounPlural.toLowerCase()}`}
        />
        <select
          className="select"
          value={state}
          onChange={(e) => setParam('state', e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="IN_REVIEW">In review</option>
          <option value="RETURNED">Returned</option>
          <option value="APPROVED">Approved</option>
        </select>
        {meta.usesStages && (
          <select
            className="select"
            value={stage}
            onChange={(e) => setParam('stage', e.target.value)}
            aria-label="Filter by stage"
          >
            <option value="">All stages</option>
            <option value="IFR">IFR — Issued for Review</option>
            <option value="IFD">IFD — Issued for Design</option>
            <option value="IFA">IFA — Issued for Approval</option>
            <option value="IFC">IFC — Issued for Construction</option>
          </select>
        )}
      </div>

      {error && <ErrorState message={error} onRetry={load} />}
      {!error && rows === null && <Loading />}

      {!error && rows !== null && rows.length === 0 && (
        <Empty
          title="Nothing here yet"
          body={q || state || stage ? 'Nothing matches these filters. Try clearing them.' : meta.emptyBody}
          action={
            (q || state || stage) && (
              <button className="btn btn-ghost" onClick={() => setParams({}, { replace: true })}>
                Clear filters
              </button>
            )
          }
        />
      )}

      {!error && rows !== null && rows.length > 0 && !meta.groupByDiscipline && (
        <div className="tbl-wrap">
          <table className="tbl">
            <Head />
            <tbody>
              <Rows list={rows} />
            </tbody>
          </table>
        </div>
      )}

      {!error && rows !== null && rows.length > 0 && meta.groupByDiscipline && (
        <>
          {[...groups.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([code, g]) => (
              <section key={code} style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: '.8125rem', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                  {g.label} <span style={{ opacity: 0.7 }}>({g.rows.length})</span>
                </h2>
                <div className="tbl-wrap">
                  <table className="tbl">
                    <Head />
                    <tbody>
                      <Rows list={g.rows} />
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
        </>
      )}
    </>
  );
}
